import { useEffect, useState, useCallback, useRef } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { printReportSmart } from "@/lib/kioskMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users as UsersIcon,
  Clock,
  Plus,
  Pencil,
  Trash2,
  PlayCircle,
  StopCircle,
  Printer,
  FileDown,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("el-GR");
  } catch {
    return iso;
  }
}

function fmtEUR(n) {
  return (Number(n) || 0).toLocaleString("el-GR", { style: "currency", currency: "EUR" });
}

export default function ShiftsAndUsers() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";

  const [shift, setShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCash, setOpenCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeReport, setCloseReport] = useState(null); // dialog payload after close
  const [settings, setSettings] = useState(null);
  const [printers, setPrinters] = useState([]);

  // user dialog
  const [userDialog, setUserDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = { name: "", pin: "", role: "cashier", sale_class_access: "ALL", active: true };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [a, b, c, s, pr] = await Promise.all([
        http.get("/shifts/current"),
        http.get("/shifts?limit=30"),
        isAdmin ? http.get("/users") : Promise.resolve({ data: [] }),
        http.get("/settings"),
        http.get("/printers"),
      ]);
      setShift(a.data?.shift || null);
      setShifts(b.data || []);
      setUsers(c.data || []);
      setSettings(s.data || null);
      setPrinters(pr.data || []);
    } catch {
      /* noop */
    }
  }, [isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  const openShift = async () => {
    setBusy(true);
    try {
      const r = await http.post("/shifts/open", { cash_start: parseFloat(openCash) || 0 });
      setShift(r.data);
      setOpenCash("");
      toast.success("Άνοιξε η βάρδια");
      load();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const closeShift = async () => {
    setBusy(true);
    try {
      // fetch report BEFORE closing (while shift is still active)
      const rep = await http.get(`/shifts/${shift.id}/report`);
      await http.post("/shifts/close", { notes: closeNotes || null });
      toast.success("Έκλεισε η βάρδια");
      setCloseReport(rep.data);
      setCloseNotes("");
      setShift(null);
      load();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const startNewUser = () => {
    setEditingId(null);
    setForm(empty);
    setUserDialog(true);
  };

  const startEditUser = (u) => {
    setEditingId(u.id);
    setForm({ ...empty, ...u, pin: "" });
    setUserDialog(true);
  };

  const saveUser = async () => {
    try {
      if (editingId) {
        const patch = { ...form };
        if (!patch.pin) delete patch.pin;
        await http.put(`/users/${editingId}`, patch);
        toast.success("Αποθηκεύτηκε");
      } else {
        if (!form.pin || form.pin.length < 4) {
          toast.error("PIN τουλάχιστον 4 ψηφία");
          return;
        }
        await http.post("/users", form);
        toast.success("Δημιουργήθηκε");
      }
      setUserDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const delUser = async (u) => {
    if (!(await confirmDialog(`Διαγραφή χρήστη «${u.name}»;`))) return;
    try {
      await http.delete(`/users/${u.id}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const expectedCash = 0;  // not tracked anymore


  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 bg-grid">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Clock size={20} /> Βάρδιες & Χρήστες
        </h2>
        <div className="text-xs text-slate-500">
          Διαχείριση βαρδιών (άνοιγμα/κλείσιμο με μετρητά) και χρηστών συστήματος.
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 space-y-6">
        {/* Current shift */}
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock size={18} /> Τρέχουσα Βάρδια
          </h3>
          {!shift ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">Δεν υπάρχει ανοιχτή βάρδια.</div>
              <div className="flex items-end gap-3 max-w-md">
                <div className="flex-1">
                  <Label className="text-xs">Αρχικό Ταμείο (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={openCash}
                    onChange={(e) => setOpenCash(e.target.value)}
                    placeholder="0.00"
                    className="h-10"
                    data-testid="shift-open-cash"
                  />
                </div>
                <Button
                  onClick={openShift}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 h-10"
                  data-testid="shift-open-btn"
                >
                  <PlayCircle size={16} className="mr-1.5" /> Άνοιγμα Βάρδιας
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Stat label="Άνοιγμα" value={fmtDate(shift.opened_at)} />
                <Stat label="Αρχικό Ταμείο" value={fmtEUR(shift.cash_start)} />
                <Stat label="Πωλήσεις AA" value={fmtEUR(shift.sales_aa)} color="text-blue-700" />
                <Stat label="Πωλήσεις BB" value={fmtEUR(shift.sales_bb)} color="text-violet-700" />
                <Stat label="Σύνολο" value={fmtEUR(shift.sales_total)} color="text-emerald-700" big />
                <Stat label="Αποδείξεις" value={shift.receipts_count} />
              </div>
              <div className="border-t border-slate-200 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="text-xs">Σημείωση (προαιρετικά)</Label>
                  <Input value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} className="h-10" />
                </div>
                <Button
                  onClick={closeShift}
                  disabled={busy}
                  className="bg-red-600 hover:bg-red-700 h-10"
                  data-testid="shift-close-btn"
                >
                  <StopCircle size={16} className="mr-1.5" /> Κλείσιμο Βάρδιας
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Shifts history */}
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-3">Ιστορικό Βαρδιών</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-xs uppercase tracking-wider">
                  <th className="text-left p-2">Ταμίας</th>
                  <th className="text-left p-2">Άνοιγμα</th>
                  <th className="text-left p-2">Κλείσιμο</th>
                  <th className="text-right p-2">Αρχ. Ταμείο</th>
                  <th className="text-right p-2">Πωλ. AA</th>
                  <th className="text-right p-2">Πωλ. BB</th>
                  <th className="text-right p-2">Μετρημένα</th>
                  <th className="text-right p-2">Διαφορά</th>
                </tr>
              </thead>
              <tbody>
                {shifts.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-4">Καμία εγγραφή</td></tr>
                )}
                {shifts.map((s) => {
                  const expected = Number(s.cash_start || 0) + Number(s.sales_aa || 0);
                  const diff = s.cash_counted !== null && s.cash_counted !== undefined
                    ? Number(s.cash_counted) - expected
                    : null;
                  return (
                    <tr key={s.id} className="border-t border-slate-100">
                      <td className="p-2 font-bold">{s.user_name}</td>
                      <td className="p-2 font-mono text-xs">{fmtDate(s.opened_at)}</td>
                      <td className="p-2 font-mono text-xs">
                        {s.closed_at ? fmtDate(s.closed_at) : <span className="text-emerald-600 font-bold">ανοιχτή</span>}
                      </td>
                      <td className="p-2 text-right font-mono">{fmtEUR(s.cash_start)}</td>
                      <td className="p-2 text-right font-mono text-blue-700">{fmtEUR(s.sales_aa)}</td>
                      <td className="p-2 text-right font-mono text-violet-700">{fmtEUR(s.sales_bb)}</td>
                      <td className="p-2 text-right font-mono">{s.cash_counted !== null && s.cash_counted !== undefined ? fmtEUR(s.cash_counted) : "—"}</td>
                      <td className={`p-2 text-right font-mono font-bold ${diff === null ? "" : diff === 0 ? "text-emerald-700" : diff > 0 ? "text-blue-700" : "text-red-700"}`}>
                        {diff === null ? "—" : fmtEUR(diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Users (admin only) */}
        {isAdmin && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <UsersIcon size={18} /> Χρήστες
              </h3>
              <Button onClick={startNewUser} className="h-9" data-testid="user-new-btn">
                <Plus size={14} className="mr-1.5" /> Νέος Χρήστης
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-xs uppercase tracking-wider">
                    <th className="text-left p-2">Όνομα</th>
                    <th className="text-left p-2">Ρόλος</th>
                    <th className="text-left p-2">Πρόσβαση Αναφορών</th>
                    <th className="text-center p-2">Ενεργός</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="p-2 font-bold">{u.name}</td>
                      <td className="p-2"><RoleBadge role={u.role} /></td>
                      <td className="p-2 font-mono text-xs">{u.sale_class_access || "ALL"}</td>
                      <td className="p-2 text-center">{u.active ? "✓" : "—"}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => startEditUser(u)} data-testid={`user-edit-${u.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => delUser(u)} className="text-red-600" data-testid={`user-del-${u.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* User dialog */}
      <Dialog open={userDialog} onOpenChange={setUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Διόρθωση Χρήστη" : "Νέος Χρήστης"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Όνομα</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs">{editingId ? "Νέο PIN (αφήστε κενό για διατήρηση)" : "PIN (4-10 ψηφία)"}</Label>
              <Input
                type="password"
                inputMode="numeric"
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value })}
                placeholder={editingId ? "(αμετάβλητο)" : "π.χ. 1234"}
                data-testid="user-pin"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ρόλος</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-10" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashier">Ταμίας</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Πρόσβαση Αναφορών</Label>
                <Select
                  value={form.sale_class_access}
                  onValueChange={(v) => setForm({ ...form, sale_class_access: v })}
                >
                  <SelectTrigger className="h-10" data-testid="user-access"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Όλες (AA + BB)</SelectItem>
                    <SelectItem value="AA">Μόνο AA</SelectItem>
                    <SelectItem value="BB">Μόνο BB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="user-active"
              />
              <span className="text-sm">Ενεργός</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialog(false)}>Άκυρο</Button>
            <Button onClick={saveUser} className="bg-emerald-600 hover:bg-emerald-700" data-testid="user-save">
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close-shift report dialog */}
      <ShiftCloseReportDialog
        report={closeReport}
        settings={settings}
        printers={printers}
        onClose={() => setCloseReport(null)}
      />
    </div>
  );
}

function Stat({ label, value, color = "text-slate-800", big = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`font-mono tabular font-bold ${big ? "text-2xl" : "text-lg"} ${color}`}>{value}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    admin: { bg: "bg-red-100 text-red-800 border-red-300", label: "Admin" },
    supervisor: { bg: "bg-blue-100 text-blue-800 border-blue-300", label: "Supervisor" },
    cashier: { bg: "bg-slate-100 text-slate-800 border-slate-300", label: "Ταμίας" },
  };
  const c = map[role] || map.cashier;
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${c.bg}`}>{c.label}</span>;
}

function ShiftCloseReportDialog({ report, settings, printers = [], onClose }) {
  const printRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!report) return null;
  const { shift, payments_breakdown, summary } = report;
  // Resolve the reports printer (is_reports=true), falling back to default or first active.
  const reportPrinter =
    printers.find((p) => p.is_reports && p.active) ||
    printers.find((p) => p.is_default && p.active) ||
    printers.find((p) => p.active) ||
    null;
  const printerType = reportPrinter?.printer_type || "kitchen";
  const isKitchen = printerType !== "a4";
  const bizLines = settings?.business_lines || [];
  const bizBold = settings?.business_lines_bold || [];

  // Header HTML (preview + printed). Always Arial. Per-line bold flags.
  const headerHtml = bizLines
    .map((s, i) => {
      const t = (s || "").trim();
      if (!t) return "";
      const bold = !!bizBold[i];
      return `<div class="biz${bold ? " bold" : ""}">${escapeHtml(t)}</div>`;
    })
    .join("");

  const onPrint = () => {
    if (!printRef.current) return;
    const pageCss = isKitchen
      ? `@page { size: 80mm auto; margin: 4mm; } body { width: 76mm; font-size: 12px; }`
      : `@page { size: A4; margin: 18mm; } body { width: auto; font-size: 13px; }`;
    const html = `<html><head><title>Αναφορά Βάρδιας</title>
      <style>
        ${pageCss}
        body { font-family: Arial, "Helvetica Neue", Helvetica, sans-serif; padding: 8px; color:#000; margin:0; }
        .header { text-align:center; padding-bottom:6px; border-bottom:1px dashed #000; margin-bottom:8px; }
        .biz { font-size: ${isKitchen ? "12px" : "13px"}; line-height: 1.25; }
        .biz.bold { font-weight: 900; }
        h1 { font-size: ${isKitchen ? "14px" : "18px"}; margin: 8px 0 4px; text-align:center; letter-spacing:1px; }
        h2 { font-size: ${isKitchen ? "12px" : "13px"}; margin: 8px 0 4px; border-bottom: 1px dashed #000; }
        table { width: 100%; border-collapse: collapse; font-size: ${isKitchen ? "12px" : "13px"}; }
        td, th { padding: 2px 4px; text-align: left; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        .tot { font-weight: bold; border-top: 1px solid #000; }
        .row { display:flex; justify-content:space-between; font-size: ${isKitchen ? "12px" : "13px"}; line-height:1.3; }
        .row .lbl { color:#555; }
        .row .val { font-variant-numeric: tabular-nums; }
        .row .val.bold { font-weight:900; }
        .footer { text-align:center; font-size:10px; color:#666; margin-top:10px; border-top:1px solid #ddd; padding-top:6px; }
      </style></head><body>
      <div class="header">${headerHtml}<h1>ΑΝΑΦΟΡΑ ΚΛΕΙΣΙΜΑΤΟΣ ΒΑΡΔΙΑΣ</h1></div>
      ${buildReportBodyHtml({ shift, payments_breakdown, summary })}
      <div class="footer">Εκτυπώθηκε: ${new Date().toLocaleString("el-GR")}</div>
      </body></html>`;
    // Companion-first: αν τρέχει τοπικός agent, στέλνουμε text version
    // για silent ESC/POS printing. Αλλιώς fallback στο hidden iframe.
    const plainText = buildReportPlainText({
      bizLines, bizBold, shift, payments_breakdown, summary,
    });
    printReportSmart({
      text: plainText, html,
      route: reportPrinter?.name || "",
      afterMs: 300,
    });
  };

  const onPdf = async () => {
    if (!printRef.current) return;
    setBusy(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      const canvas = await html2canvas(printRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const pdf = isKitchen
        ? new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: [80, Math.max(120, (canvas.height * 76) / canvas.width + 8)],
          })
        : new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = isKitchen ? 2 : 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, imgW, imgH);
      const dt = new Date().toISOString().slice(0, 10);
      pdf.save(`shift-${shift?.id?.slice(0, 8)}-${dt}.pdf`);
      toast.success("Αποθηκεύτηκε σε PDF");
    } catch (e) {
      toast.error("Αποτυχία PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent hideClose className="max-w-md p-0 overflow-hidden">
        <div
          ref={printRef}
          className="p-5 space-y-3"
          style={{ fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif' }}
        >
          {/* Business header (per-line bold) */}
          {bizLines.some((s) => (s || "").trim()) && (
            <div className="text-center border-b border-slate-300 pb-2 space-y-0.5">
              {bizLines.map((s, i) => {
                const t = (s || "").trim();
                if (!t) return null;
                return (
                  <div
                    key={i}
                    className={`text-sm ${bizBold[i] ? "font-extrabold" : ""}`}
                  >
                    {t}
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center border-b border-slate-300 pb-2">
            <h1 className="text-lg font-bold tracking-wide">ΑΝΑΦΟΡΑ ΚΛΕΙΣΙΜΑΤΟΣ ΒΑΡΔΙΑΣ</h1>
            <div className="text-xs text-slate-600">
              Ponte Pos · {isKitchen ? "80mm" : "A4"}
              {reportPrinter && <> · {reportPrinter.name}</>}
            </div>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Ταμίας:</span><span className="font-bold">{shift?.user_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Άνοιγμα:</span><span className="font-mono text-xs">{fmtDate(shift?.opened_at)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Κλείσιμο:</span><span className="font-mono text-xs">{fmtDate(new Date().toISOString())}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Αρχικό Ταμείο:</span><span className="font-mono">{fmtEUR(shift?.cash_start || 0)}</span></div>
          </div>

          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b border-slate-300 pb-1 mb-1">Πωλήσεις</h2>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Αποδείξεις AA (ταμειακή):</span><span className="font-mono">{summary?.receipts_aa || 0}</span></div>
              <div className="flex justify-between"><span>Αποδείξεις BB (δισκέτα):</span><span className="font-mono">{summary?.receipts_bb || 0}</span></div>
              <div className="flex justify-between"><span>Είδη Πωληθέντα:</span><span className="font-mono">{summary?.items_count || 0}</span></div>
            </div>
          </div>

          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b border-slate-300 pb-1 mb-1">Ανάλυση Πληρωμών</h2>
            <table className="w-full text-sm">
              <tbody>
                {(payments_breakdown || []).length === 0 && (
                  <tr><td colSpan={2} className="text-center text-slate-400 py-2">Καμία πληρωμή</td></tr>
                )}
                {(payments_breakdown || []).map((p) => (
                  <tr key={p.name} className="border-b border-slate-100">
                    <td className="py-1">{p.name}</td>
                    <td className="py-1 text-right font-mono font-bold">{fmtEUR(p.amount)}</td>
                  </tr>
                ))}
                <tr className="font-bold border-t-2 border-slate-700">
                  <td className="py-1 pt-2">ΣΥΝΟΛΟ:</td>
                  <td className="py-1 pt-2 text-right font-mono text-emerald-700 text-lg">{fmtEUR(summary?.total || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-center text-[10px] text-slate-400 pt-2 border-t border-slate-200">
            Εκτυπώθηκε: {new Date().toLocaleString("el-GR")}
          </div>
        </div>

        <div className="px-5 pb-4 pt-2 bg-slate-50 grid grid-cols-3 gap-2">
          <Button onClick={onPrint} className="bg-blue-600 hover:bg-blue-700 h-10" data-testid="close-report-print">
            <Printer size={14} className="mr-1" /> Εκτύπωση
          </Button>
          <Button onClick={onPdf} disabled={busy} variant="outline" className="h-10 border-emerald-300 text-emerald-700" data-testid="close-report-pdf">
            <FileDown size={14} className="mr-1" /> {busy ? "PDF..." : "PDF"}
          </Button>
          <Button onClick={onClose} variant="outline" className="h-10" data-testid="close-report-close">
            <XIcon size={14} className="mr-1" /> Κλείσιμο
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Build the report body (without business header) for the printed window. */
function buildReportBodyHtml({ shift, payments_breakdown, summary }) {
  const row = (lbl, val, bold = false) =>
    `<div class="row"><span class="lbl">${escapeHtml(lbl)}</span><span class="val${bold ? " bold" : ""}">${escapeHtml(val)}</span></div>`;
  const fmt = (n) =>
    (Number(n) || 0).toLocaleString("el-GR", { style: "currency", currency: "EUR" });
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleString("el-GR") : "—");

  const pmRows = (payments_breakdown || [])
    .map(
      (p) =>
        `<tr><td>${escapeHtml(p.name)}</td><td class="r"><b>${escapeHtml(fmt(p.amount))}</b></td></tr>`,
    )
    .join("");

  return `
    ${row("Ταμίας:", shift?.user_name || "—", true)}
    ${row("Άνοιγμα:", fmtD(shift?.opened_at))}
    ${row("Κλείσιμο:", fmtD(new Date().toISOString()))}
    ${row("Αρχικό Ταμείο:", fmt(shift?.cash_start || 0))}
    <h2>ΠΩΛΗΣΕΙΣ</h2>
    ${row("Αποδείξεις AA:", String(summary?.receipts_aa || 0))}
    ${row("Αποδείξεις BB:", String(summary?.receipts_bb || 0))}
    ${row("Είδη πωληθέντα:", String(summary?.items_count || 0))}
    <h2>ΑΝΑΛΥΣΗ ΠΛΗΡΩΜΩΝ</h2>
    <table>
      <tbody>
        ${pmRows || `<tr><td colspan="2" style="text-align:center;color:#888;padding:6px">Καμία πληρωμή</td></tr>`}
        <tr class="tot"><td><b>ΣΥΝΟΛΟ</b></td><td class="r"><b>${escapeHtml(fmt(summary?.total || 0))}</b></td></tr>
      </tbody>
    </table>
  `;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Plain-text version για τον Companion agent (46-char 80mm layout). */
function buildReportPlainText({ bizLines, bizBold, shift, payments_breakdown, summary }) {
  const W = 46;
  const center = (s) => {
    const t = String(s || "");
    if (t.length >= W) return t.slice(0, W);
    const pad = Math.floor((W - t.length) / 2);
    return " ".repeat(pad) + t;
  };
  const sep = (ch = "-") => ch.repeat(W);
  const lr = (l, r) => {
    const L = String(l || "");
    const R = String(r || "");
    const gap = Math.max(1, W - L.length - R.length);
    return L + " ".repeat(gap) + R;
  };
  const fmt = (n) =>
    `${(Number(n) || 0).toFixed(2).replace(".", ",")} EUR`;
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleString("el-GR") : "—");
  const lines = [];
  // Επιχείρηση (bold flag ignored in plain text — companion agent handles font mods)
  (bizLines || []).forEach((s, i) => {
    const t = (s || "").trim();
    if (t) lines.push(center(bizBold?.[i] ? t.toUpperCase() : t));
  });
  lines.push(sep("="));
  lines.push(center("ΑΝΑΦΟΡΑ ΚΛΕΙΣΙΜΑΤΟΣ ΒΑΡΔΙΑΣ"));
  lines.push(sep("="));
  lines.push(lr("Ταμίας:", shift?.user_name || "—"));
  lines.push(lr("Άνοιγμα:", fmtD(shift?.opened_at)));
  lines.push(lr("Κλείσιμο:", fmtD(new Date().toISOString())));
  lines.push(lr("Αρχικό Ταμείο:", fmt(shift?.cash_start || 0)));
  lines.push(sep("-"));
  lines.push(center("ΠΩΛΗΣΕΙΣ"));
  lines.push(sep("-"));
  lines.push(lr("Αποδείξεις AA:", String(summary?.receipts_aa || 0)));
  lines.push(lr("Αποδείξεις BB:", String(summary?.receipts_bb || 0)));
  lines.push(lr("Είδη πωληθέντα:", String(summary?.items_count || 0)));
  lines.push(sep("-"));
  lines.push(center("ΑΝΑΛΥΣΗ ΠΛΗΡΩΜΩΝ"));
  lines.push(sep("-"));
  if (!payments_breakdown?.length) {
    lines.push(center("— Καμία πληρωμή —"));
  } else {
    payments_breakdown.forEach((p) => {
      lines.push(lr(p.name || "—", fmt(p.amount)));
    });
  }
  lines.push(sep("="));
  lines.push(lr("ΣΥΝΟΛΟ:", fmt(summary?.total || 0)));
  lines.push(sep("="));
  lines.push(center(`Εκτυπώθηκε: ${new Date().toLocaleString("el-GR")}`));
  lines.push("");
  lines.push("");
  return lines.join("\n");
}
